/**
 * A list of typed assets, editable — the one place the app knows how to say "what is crossing".
 *
 * It was written inside the filing form and then wanted a second time by the counter composer,
 * which had been shipped with two free-text boxes. Free text is what this whole page exists to
 * replace: eleven years of "Bijan + 2026 3rd for ₣12" in a channel is the reason nobody can answer
 * whose ninth-rounder that is. A counter is a trade offer, so it is composed the same way a trade
 * is, and accepting one has to produce a record the ledger can take without a human re-typing it.
 *
 * `holdings` is what makes the pick row honest: it lists the picks that GM actually holds, and
 * shows the origin selector only in a round where he holds two. Pass an empty list and the pick
 * button still works — a bystander offering a pick he has already traded away is a mistake the
 * league can catch, but a form that refuses to describe it is not better than one that does.
 */

import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { managerShort } from '../domain/identity.js';
import { faab } from '../domain/faab.js';
import { nameKey } from '../domain/names.js';
import { originLabel, type Holding } from '../domain/picks2026.js';
import { pickLabel, slotOf } from '../domain/picks.js';
import { pickCoords } from '../domain/tradeWindow.js';
import { LEAGUE_2026 } from '../domain/config.js';
import { BUNDLED_BANK, searchPlayers, teamLabel } from '../domain/playerBank.js';
import { BUNDLED_PROSPECTS, norm as normProspect, searchProspects } from '../domain/prospectBank.js';
import { type TradeAsset } from '../domain/tradeWindow.js';
import type { Controlled } from '../domain/tradeRoster.js';
import { bareName, positionOf } from './TradeAssets.js';
import { PlayerClubChip, PositionChip } from './chips.js';

const SEASON = 2026;
const TEAMS = LEAGUE_2026.teams;

/**
 * The buttons, in the order the design lists them.
 *
 * FARM RIGHTS HAVE NO BUTTON OF THEIR OWN any more. Tim, 2 September: "combine farm rights into
 * the player selection." One Player button opens one list — his rostered men and his farm rights
 * together, each row saying which it is — and the asset's KIND follows the row he picks, so a
 * farm player still saves as `rights` and everything downstream (the farm cap, the ledger, the
 * chips) keeps telling the two apart. The `rights` kind stays in the type; only the button went.
 */
export const ADD: ReadonlyArray<{ kind: TradeAsset['kind']; label: string }> = [
  { kind: 'player', label: '+ Player' },
  { kind: 'pick', label: '+ Pick' },
  { kind: 'faab', label: '+ \u20a3' },
  { kind: 'clause', label: '+ Other' },
];

/**
 * The rows plus the buttons under them.
 *
 * `kinds` exists because a counter cannot carry a clause: "Other consideration" is a sentence two
 * men agreed to in the open, and a private offer containing one would be a side agreement nobody
 * else can see. The filing form takes all five; the composer takes four.
 */
export function AssetStack(
  { assets, onAssets, holdings, who, kinds = ADD, disabled = false, order = [], keeperOn, own }: {
    assets: TradeAsset[];
    onAssets: (a: TradeAsset[]) => void;
    holdings: readonly Holding[];
    who: string;
    kinds?: ReadonlyArray<{ kind: TradeAsset['kind']; label: string }>;
    disabled?: boolean;
    /**
     * The published first-round order. With it, a pick is named by its code — `10.03` — and each
     * of two picks in a round is its own option. Without it the round is all there is to say.
     */
    order?: readonly string[];
    /** `round:SEAT` -> the keeper charged to that pick, for naming him beside it. */
    keeperOn?: ReadonlyMap<string, string>;
    /**
     * What this GM actually controls — rostered players and farm rights — offered before the
     * bank. See `domain/tradeRoster.ts`. Absent, the field falls back to the bank alone.
     */
    own?: readonly Controlled[];
  },
) {
  const set = (i: number, a: TradeAsset) => onAssets(assets.map((x, j) => (j === i ? a : x)));
  return (
    <>
      {assets.map((a, i) => (
        <AssetRow
          key={`${a.kind}-${i}`}
          a={a} holdings={holdings} who={who} order={order} keeperOn={keeperOn} own={own}
          onChange={(next) => set(i, next)}
          onRemove={() => onAssets(assets.filter((_, j) => j !== i))} />
      ))}
      <div className="tradd">
        {kinds.map((b) => (
          <button type="button" key={b.kind} className={`traddbtn ${b.kind}`}
            disabled={disabled}
            onClick={() => onAssets([...assets, blank(b.kind, holdings, order)])}>
            {b.label}
          </button>
        ))}
      </div>
    </>
  );
}

export function blank(
  kind: TradeAsset['kind'], holdings: readonly Holding[], order: readonly string[] = [],
): TradeAsset {
  if (kind === 'pick') {
    const first = holdings[0];
    return first
      ? { kind, text: pickText(first, order), origin: first.from }
      : { kind, text: '' };
  }
  if (kind === 'faab') return { kind, text: faab(0), amount: 0 };
  return { kind, text: '' };
}

const ORD = ['', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th',
  '11th', '12th', '13th', '14th', '15th', '16th'];

/**
 * How a pick is written on the trade: `2026 10th` until the order is published, `2026 10.03`
 * after. `pickCoords` reads both, so the ledger and the board can take either.
 *
 * THE CODE IS THE PICK'S OWN NAME, and it is what the league says out loud on draft night. "2026
 * 10th" was the best the app could do while slates were declared before the lottery; once the
 * order exists, offering a round where a code is known is offering less than the board shows.
 */
const pickText = (h: Holding, order: readonly string[] = []) => {
  const slot = order.length ? slotOf(order, h.from) : null;
  return slot === null
    ? `${SEASON} ${ORD[h.round] ?? `${h.round}th`}`
    : `${SEASON} ${pickLabel(h.round, slot, TEAMS)}`;
};

/** One option per pick he holds — `10.03 · from Rajan · Waddle keeps here`. */
const pickOption = (
  h: Holding, who: string, order: readonly string[], keeperOn?: ReadonlyMap<string, string>,
) => {
  const parts = [pickText(h, order).replace(`${SEASON} `, '')];
  if (!order.length || h.from.toUpperCase() !== who.toUpperCase()) {
    parts.push(originLabel(h, who, managerShort));
  }
  const keeper = keeperOn?.get(`${h.round}:${h.from.toUpperCase()}`);
  if (keeper) parts.push(`${managerShort(who)} keeps ${bareName(keeper)} here`);
  return parts.join(' · ');
};

const keyOf = (h: Holding) => `${h.round}:${h.from.toUpperCase()}`;

/** The option value for an asset the holdings do not know about. No round is ever `-`. */
const UNLISTED = '-:-';

/**
 * One editable asset.
 *
 * The pick row is the interesting one. A round the GM holds once needs no origin — saying "Ryan's
 * own" beside his only third is noise. A round he holds twice needs it, and the selector appears
 * only there. That is the design's rule and it is derived from the holdings, not configured.
 */
function AssetRow(
  { a, holdings, who, order = [], keeperOn, own, onChange, onRemove }: {
    a: TradeAsset; holdings: readonly Holding[]; who: string;
    order?: readonly string[]; keeperOn?: ReadonlyMap<string, string>;
    own?: readonly Controlled[];
    onChange: (a: TradeAsset) => void; onRemove: () => void;
  },
) {
  if (a.kind === 'pick') {
    /*
     * ONE OPTION PER PICK HE HOLDS, not one per round. The old shape was a round selector plus a
     * second selector for the origin that only appeared where a man held two — which was right
     * for the text "2026 9th" and wrong the moment the pick has a code: John's own 9th and the one
     * he got from Rajan are 9.02 and 9.07, and a list that says "9th" twice is hiding the thing
     * that tells them apart. The option value is the pick's identity (round and seat), the label
     * is its code, its origin where that is not obvious, and the keeper standing on it.
     */
    const current = holdings.find((h) => keyOf(h) === `${pickCoords(a.text)?.round ?? 0}:${(a.origin ?? who).toUpperCase()}`)
      ?? holdings.find((h) => pickText(h, order) === a.text);
    /*
     * A ROW ALWAYS SHOWS WHAT IT SAYS (Tim, 8 September: "when I add a pick to a trade or counter
     * offer, the value is not visible").
     *
     * The select's value is the pick's identity, matched back against the holdings — and where
     * that match failed the value was `''`, which no option carries, so the control drew EMPTY
     * over an asset that has a perfectly good text on it. That happens whenever the holdings and
     * the asset disagree: a bystander composing against a list the app cannot compute for him, a
     * pick already traded on, a counter reopened after the board moved. The asset is the record
     * and the holdings are a convenience, so the asset wins: its own text goes in as an option of
     * its own, marked, rather than being silently swallowed.
     */
    const unlisted = current || !a.text ? null : a.text.replace(`${SEASON} `, '');
    const value = current ? keyOf(current) : unlisted ? UNLISTED : '';
    const keeper = current ? keeperOn?.get(keyOf(current)) : undefined;
    return (
      <span className={`trasset pick editing${keeper ? ' occupied' : ''}`}>
        <span className="trkind">Pick</span>
        <select className="trpicksel" value={value}
          onChange={(e) => {
            const h = holdings.find((x) => keyOf(x) === e.target.value);
            if (h) onChange({ ...a, text: pickText(h, order), origin: h.from });
          }}>
          {unlisted && <option value={UNLISTED}>{unlisted}</option>}
          {holdings.map((h) => (
            <option key={keyOf(h)} value={keyOf(h)}>{pickOption(h, who, order, keeperOn)}</option>
          ))}
        </select>
        <span className="spacer" />
        <button type="button" className="trx" onClick={onRemove} aria-label="Remove">×</button>
      </span>
    );
  }

  if (a.kind === 'faab') {
    return (
      <span className="trasset faab editing">
        <span className="trkind">FAAB</span>
        <input type="number" min={0} step={1} value={a.amount ?? 0}
          onChange={(e) => {
            const n = Math.max(0, Math.round(Number(e.target.value) || 0));
            onChange({ ...a, amount: n, text: faab(n) });
          }} />
        <span className="spacer" />
        <button type="button" className="trx" onClick={onRemove} aria-label="Remove">×</button>
      </span>
    );
  }

  const placeholder = a.kind === 'player' ? 'Player, or farm rights'
    : a.kind === 'rights' ? 'Farm player — rights only'
    : 'Other consideration, in your own words';
  /*
   * THE KIND FOLLOWS THE ROW. A name picked off his own list carries what it is; a farm player
   * becomes a `rights` asset and a rostered man a `player`, whichever button opened the row.
   * Free text stays whatever the row already was.
   */
  const set = (text: string) => {
    const picked = own?.find((p) => nameKey(p.name) === nameKey(text));
    const kind = picked ? picked.kind : a.kind;
    onChange({
      ...a,
      kind,
      text,
      ...(kind === 'player' ? { position: positionOf(text) ?? undefined } : { position: undefined }),
    });
  };
  return (
    <span className={`trasset ${a.kind} editing`}>
      <span className="trkind">{a.kind === 'clause' ? 'Clause' : a.kind === 'rights' ? 'Farm rights' : 'Player'}</span>
      {/*
        The chips are drawn from the PLAYER RECORD as he types, not from the trade. A name that
        resolves gets its position and club immediately; a name that does not gets nothing, and
        the missing chip is the signal that the spelling is off before the trade is filed rather
        than after it is in the ledger.
      */}
      {a.kind === 'player' && a.text && (
        <>
          <PositionChip pos={positionOf(a.text)} />
          <PlayerClubChip player={a.text} />
          {/* A declared keeper travels with his standing (2013-III-3): say so on the row. */}
          {own?.find((p) => nameKey(p.name) === nameKey(a.text))?.keeper && (
            <span className="chip warning tiny" title="A declared keeper — his keeper standing and cost go with him (2013-III-3, 2018-III-2)">
              keeper · Rd {own.find((p) => nameKey(p.name) === nameKey(a.text))!.costRound}
            </span>
          )}
        </>
      )}
      {a.kind === 'player' || a.kind === 'rights'
        ? <NameField kind={a.kind} value={a.text} placeholder={placeholder} onText={set}
          own={own} />
        : (
          <input type="text" value={a.text} placeholder={placeholder}
            onChange={(e) => set(e.target.value)} />
        )}
      <button type="button" className="trx" onClick={onRemove} aria-label="Remove">×</button>
    </span>
  );
}

/* ------------------------------------------------------------------------- naming the asset */

/**
 * The name field, with the bank behind it.
 *
 * WHY THIS IS NOT A COSMETIC ADDITION. Everything downstream of a filed trade keys on the name:
 * `positionOf` and `PlayerClubChip` look the player up to draw his chips, the keeper screens
 * match him against a roster, and the archive is searched by it. A typed "Marvin Harrison" that
 * should be "Marvin Harrison Jr" resolves to nobody, draws no chips, and quietly becomes a row
 * the rest of the app cannot join to anything — and it is discovered in February, by which point
 * the trade is the record. The bank is right there; the form should not make somebody spell out
 * of memory what it can offer him.
 *
 * IT SUGGESTS, IT DOES NOT ENFORCE. Free text still saves. The league has traded players the bank
 * has never heard of — a rookie in the hours before the feed catches up, and eleven years of
 * archive names — and a field that refused them would be a filing form that cannot file the
 * trade two men just agreed to. The missing chip stays the signal, exactly as before.
 *
 * TWO BANKS, ONE FIELD. A player row searches the NFL bank; a farm-rights row searches the
 * prospect bank, because farm rights are held on college and high-school players who are not in
 * the NFL bank at all — suggesting from the wrong one would be worse than suggesting nothing.
 */
const SUGGEST_LIMIT = 8;
/* One character matches half the league; the list is noise until the second. */
const SUGGEST_FROM = 2;

interface Suggestion { name: string; pos: string | null; note: string }

export function nameSuggestions(
  kind: 'player' | 'rights', typed: string, own: readonly Controlled[] = [],
): Suggestion[] {
  const q = bareName(typed).trim();
  /*
   * WHAT HE HOLDS COMES FIRST, and it comes before he has typed anything. A trade form is not a
   * search box: the question is "which of your men", and the answer is a list short enough to
   * read. Typing narrows it; the bank is appended only once there is something to search for.
   */
  const mineAll = own.filter((p) => nameKey(p.name) !== nameKey(q));
  const mine = q.length === 0
    ? mineAll
    : mineAll.filter((p) => nameKey(p.name).includes(nameKey(q)));
  const ownRows: Suggestion[] = mine.map((p) => ({ name: p.name, pos: p.pos, note: p.note || 'on your roster' }));
  if (q.length < SUGGEST_FROM) return ownRows.slice(0, own.length ? 40 : 0);
  const seen = new Set(ownRows.map((r) => nameKey(r.name)));
  const fromBank = (list: Suggestion[]) => list.filter((x) => !seen.has(nameKey(x.name)));
  /* One list means one fallback: the NFL bank first, the prospect bank behind it. */
  const bank = kind === 'rights'
    ? prospectSuggestions(q)
    : [...playerSuggestions(q), ...prospectSuggestions(q).slice(0, 3)];
  return [...ownRows, ...fromBank(bank)].slice(0, Math.max(SUGGEST_LIMIT, ownRows.length + 3));
}

function prospectSuggestions(q: string): Suggestion[] {
  const settled = (list: Suggestion[]) => list.filter((x) => nameKey(x.name) !== nameKey(q));
  const k = normProspect(q);
  const rank = (n: string) => {
    const x = normProspect(n);
    return x.startsWith(k) ? 2 : x.includes(k) ? 1 : 0;
  };
  return settled(searchProspects(BUNDLED_PROSPECTS, q)
    .slice()
    .sort((x, y) => rank(y.n) - rank(x.n)
      || (x.overall ?? 9999) - (y.overall ?? 9999)
      || x.n.localeCompare(y.n))
    .slice(0, SUGGEST_LIMIT)
    .map((p) => ({ name: p.n, pos: p.p, note: p.hs ? `${p.hsSchool ?? p.s} (HS)` : p.s })));
}

function playerSuggestions(q: string): Suggestion[] {
  const settled = (list: Suggestion[]) => list.filter((x) => nameKey(x.name) !== nameKey(q));
  return settled(searchPlayers(BUNDLED_BANK, q, { limit: SUGGEST_LIMIT })
    .map((p) => ({ name: p.name, pos: p.pos, note: teamLabel(p) })));
}


function NameField(
  { kind, value, placeholder, onText, own = [] }: {
    kind: 'player' | 'rights'; value: string; placeholder: string; onText: (t: string) => void;
    own?: readonly Controlled[];
  },
) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [up, setUp] = useState(false);
  const listId = useId();
  const box = useRef<HTMLInputElement>(null);
  const menu = useRef<HTMLUListElement>(null);

  const list = useMemo(() => (open ? nameSuggestions(kind, value, own) : []), [kind, value, open, own]);
  const at = Math.min(active, Math.max(0, list.length - 1));

  /*
   * WHICH WAY IT OPENS, measured rather than assumed.
   *
   * On a phone the filing form is taller than the screen and the field somebody is typing in is
   * usually near the bottom of it — with the keyboard up, always. A list that only ever drops
   * downwards puts its first suggestion under the keyboard, and the one thing worse than no
   * suggestions is suggestions you cannot see or reach.
   */
  useLayoutEffect(() => {
    const el = box.current; const m = menu.current;
    if (!el || !m || list.length === 0) { setUp(false); return; }
    const r = el.getBoundingClientRect();
    const h = m.offsetHeight;
    const room = window.innerHeight - r.bottom;
    setUp(room < h + 12 && r.top > h + 12);
  }, [list.length, value]);

  const take = (name: string) => { onText(name); setOpen(false); setActive(0); };

  return (
    <span className="trfind">
      <input
        ref={box} type="text" value={value} placeholder={placeholder}
        role="combobox" aria-expanded={list.length > 0} aria-controls={listId}
        aria-autocomplete="list" autoComplete="off" spellCheck={false}
        aria-activedescendant={list.length > 0 ? `${listId}-${at}` : undefined}
        onChange={(e) => { onText(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        /*
         * The list closes on blur rather than on a click outside, and the option rows cancel
         * their own mousedown — otherwise the input loses focus first, the list unmounts, and
         * the click lands on whatever the layout collapsed into that space.
         */
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setOpen(false); return; }
          if (list.length === 0) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % list.length); }
          else if (e.key === 'ArrowUp') {
            e.preventDefault(); setActive((i) => (i - 1 + list.length) % list.length);
          } else if (e.key === 'Enter' || e.key === 'Tab') {
            const pick = list[at];
            /*
             * Enter takes the highlighted name; it does NOT submit. The filing form is one form
             * and a stray Enter used to post the trade with a half-typed name in it.
             */
            if (pick) { if (e.key === 'Enter') e.preventDefault(); take(pick.name); }
          }
        }} />
      {list.length > 0 && (
        <ul className={`trsug ${up ? 'up' : ''}`} id={listId} role="listbox" ref={menu}>
          {list.map((s, i) => (
            <li key={`${s.name}-${i}`} id={`${listId}-${i}`} role="option" aria-selected={i === at}
              className={i === at ? 'on' : ''}
              onMouseDown={(e) => { e.preventDefault(); take(s.name); }}
              onMouseEnter={() => setActive(i)}>
              <PositionChip pos={s.pos} />
              <span className="trsugname">{s.name}</span>
              <span className="trsugnote">{s.note}</span>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}
